// import GamesActionBar from '@/components/GamesActionBar/GamesActionBar';
import GameCard from '@/components/GameCard/GameCard';
import ResourcesPageHeader from '@/components/ResourcesPageHeader/ResourcesPageHeader';
import {
  fetchGamesAll,
  fetchGamesGroupedByGameGroup,
  fetchUnifiedSearch,
} from '@/lib/api';
import { getFirstGameByRTP } from '@/lib/game';
// import { useDownload } from '@/hooks/useDownload';
import {
  GameSchema,
  UnifiedSearchResponse,
  WithFormatted,
} from '@/lib/types';
import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import {
  GamesSortByOptions,
  mapFiltersToParams,
} from './ResourcePageHelpers';
import CircularProgress from '@mui/material/CircularProgress';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
// import { DownloadCategory } from '@/lib/types';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { useScrollContainer } from '@/contexts/ScrollContainerContext';
import ResourcesSearchPropositions from '@/components/ResourcesPageHeader/ResourcesSearchPropositions';
import ExportToExcelButton from '@/components/ExportToExcelButton/ExportToExcelButton';
import { ga } from '@/lib/analytics';

const validSortByValues = new Set(
  GamesSortByOptions.map((o) => o.value),
);
const defaultSortBy = GamesSortByOptions[0].value;
const defaultSortOrder = 'desc' as const;

function parseSortFromUrl(searchParams: URLSearchParams): {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
} {
  const sortBy = searchParams.get('sort_by');
  const sortOrder = searchParams.get('sort_order');
  return {
    sortBy:
      sortBy && validSortByValues.has(sortBy)
        ? sortBy
        : defaultSortBy,
    sortOrder:
      sortOrder === 'asc' || sortOrder === 'desc'
        ? sortOrder
        : defaultSortOrder,
  };
}

const GamesResourcesPage = () => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [committedSearchText, setCommittedSearchText] = useState('');
  const [gridMode, setGridMode] = useState<
    'explorer' | 'search' | 'proposition'
  >('explorer');
  const [propositionGame, setPropositionGame] =
    useState<WithFormatted<GameSchema> | null>(null);
  const [selectedGames, setSelectedGames] = useState<number[]>([]);
  // const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectAllRequested, setSelectAllRequested] = useState(false);
  const [sortBy, setSortBy] = useState<string>(
    () => parseSortFromUrl(searchParams).sortBy,
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    () => parseSortFromUrl(searchParams).sortOrder,
  );
  const filters: FilterSchema = useOutletContext();
  const scrollContainerRef = useScrollContainer();
  const searchPropositionsPopperRef = useRef<HTMLDivElement>(null);
  const gamesSortBy = sortBy === 'name' ? 'game_name' : sortBy;
  const filterParams = mapFiltersToParams(filters, gamesSortBy, {
    sort_order: sortOrder,
  });
  const trimmedCommittedSearchText = committedSearchText.trim();
  const hasSufficientCommittedSearchText =
    trimmedCommittedSearchText.length >= 2;

  const {
    data: baseGamesData,
    isLoading: isBaseLoading,
    isFetchingNextPage: isBaseFetchingNextPage,
    total_count: baseTotalCount,
  } = useInfiniteScroll({
    section: 'Games Resources',
    fetchFn: fetchGamesGroupedByGameGroup,
    queryKey: ['games', ...Object.values(filterParams)],
    params: filterParams,
    scrollContainerRef: scrollContainerRef || undefined,
    useWindowScroll: !scrollContainerRef,
    pageSize: 20,
    scrollThreshold: 200,
    extractData: (response) =>
      response.games_by_group
        .map((group) => group.games)
        .filter((groupGames) => groupGames && groupGames.length > 0),
    enabled: gridMode === 'explorer',
  });

  const {
    data: searchedGamesData,
    isLoading: isSearchLoading,
    isFetchingNextPage: isSearchFetchingNextPage,
    total_count: searchTotalCount,
  } = useInfiniteScroll({
    section: 'Games Resources Search',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedCommittedSearchText,
        ttype: ['games'],
        ...filterParams,
        limit,
        offset,
      }),
    queryKey: [
      'games-search-grid',
      trimmedCommittedSearchText,
      ...Object.values(filterParams),
    ],
    scrollContainerRef: scrollContainerRef || undefined,
    useWindowScroll: !scrollContainerRef,
    pageSize: 20,
    scrollThreshold: 200,
    enabled:
      gridMode === 'search' && hasSufficientCommittedSearchText,
    extractData: (response) =>
      response.games?.hits
        ? Object.values(response.games.hits).filter(
            (groupGames) => groupGames && groupGames.length > 0,
          )
        : [],
  });

  const data = useMemo(() => {
    if (gridMode === 'proposition') {
      return propositionGame ? [[propositionGame]] : [];
    }
    return gridMode === 'search' ? searchedGamesData : baseGamesData;
  }, [baseGamesData, gridMode, propositionGame, searchedGamesData]);

  const isLoading =
    gridMode === 'search' ? isSearchLoading : isBaseLoading;
  const isFetchingNextPage =
    gridMode === 'search'
      ? isSearchFetchingNextPage
      : isBaseFetchingNextPage;
  const total_count =
    gridMode === 'search' ? searchTotalCount : baseTotalCount;

  // Reported once the results of an accepted search are in, so that
  // `result_count` can be sent along with the search term.
  const searchAcceptSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (gridMode !== 'search' || !hasSufficientCommittedSearchText) {
      searchAcceptSentRef.current = null;
      return;
    }
    if (isSearchLoading) return;
    if (searchAcceptSentRef.current === trimmedCommittedSearchText)
      return;

    searchAcceptSentRef.current = trimmedCommittedSearchText;
    ga.trackSearchAccept({
      search_term: trimmedCommittedSearchText,
      result_count: searchTotalCount ?? searchedGamesData.length,
    });
  }, [
    gridMode,
    hasSufficientCommittedSearchText,
    isSearchLoading,
    searchTotalCount,
    searchedGamesData.length,
    trimmedCommittedSearchText,
  ]);

  const handleGameSelect = (games: GameSchema[]) => {
    const gameIds = games.map((g) => g.id);
    setSelectedGames((prev) => {
      const allSelected = gameIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !gameIds.includes(id));
      } else {
        return [
          ...prev,
          ...gameIds.filter((id) => !prev.includes(id)),
        ];
      }
    });
  };

  // const { download, loading } = useDownload();

  // The games action bar (select all + bulk download) is hidden for now.
  // The `games_bulk_download` event is wired up here so it starts reporting
  // as soon as the bar is shown again.
  // const handleDownload = (category: DownloadCategory) => {
  //   ga.trackGamesBulkDownload({
  //     total_games: selectedGames.length,
  //     filters_applied: ga.formatFiltersApplied(filters),
  //     search_term: trimmedCommittedSearchText,
  //   });
  //   download({
  //     category,
  //     game_ids: selectedGames,
  //   });
  // };

  const gamesAllQuery = useQuery({
    queryKey: ['games-all', ...Object.values(filterParams)],
    queryFn: () =>
      fetchGamesAll({
        ...filterParams,
      }),
    enabled: selectAllRequested,
  });

  useEffect(() => {
    if (!selectAllRequested) return;
    if (gamesAllQuery.isError) {
      setSelectAllRequested(false);
      // setSelectAllChecked(false);
      return;
    }
    if (!gamesAllQuery.data) return;

    const allIds = gamesAllQuery.data.games.map((g) => g.id);
    setSelectedGames(allIds);
    // setSelectAllChecked(true);
    setSelectAllRequested(false);
  }, [gamesAllQuery.data, gamesAllQuery.isError, selectAllRequested]);

  useEffect(() => {
    setSelectedGames([]);
    // setSelectAllChecked(false);
    setSelectAllRequested(false);
  }, [filters, gridMode, trimmedCommittedSearchText]);

  useEffect(() => {
    if (gamesAllQuery.isFetching) return;
    if (total_count && total_count > 0) {
      //  setSelectAllChecked(selectedGames.length === total_count);
    } else {
      // setSelectAllChecked(false);
    }
  }, [gamesAllQuery.isFetching, selectedGames.length, total_count]);

  const trimmedSearchText = searchText.trim();
  const hasSufficientSearchText = trimmedSearchText.length >= 2;

  const {
    data: searchSuggestions,
    isLoading: isSearchSuggestionsLoading,
  } = useInfiniteScroll<
    WithFormatted<GameSchema>,
    Record<string, unknown>,
    UnifiedSearchResponse
  >({
    section: 'Games Resources Search Propositions',
    fetchFn: ({ offset, limit }) =>
      fetchUnifiedSearch({
        q: trimmedSearchText,
        ttype: ['games'],
        ...filterParams,
        limit,
        offset,
      }),
    queryKey: [
      'unified-search-propositions',
      'games',
      trimmedSearchText,
      ...Object.values(filterParams),
    ],
    scrollContainerRef:
      (searchPropositionsPopperRef as React.RefObject<HTMLElement>) ||
      undefined,
    useWindowScroll: false,
    pageSize: 5,
    scrollThreshold: 80,
    enabled: hasSufficientSearchText,
    extractData: (response) =>
      response.games?.hits
        ? Object.values(response.games.hits)
            .map((groupGames) => {
              const group = groupGames;
              return (getFirstGameByRTP(
                group,
                filters.gameStatus,
                sortBy,
                sortOrder,
              ) || group[0]) as WithFormatted<GameSchema>;
            })
            .filter((g): g is WithFormatted<GameSchema> => g != null)
        : [],
  });

  const hasActiveSearchOrFilter =
    (gridMode === 'search' &&
      trimmedCommittedSearchText &&
      trimmedCommittedSearchText.length > 0) ||
    Object.values(filters).some((value) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return Boolean(value);
    });

  const handleSortByChange = (value: string) => {
    setSortBy(value);
  };

  const handleSortOrderChange = (value: 'asc' | 'desc') => {
    setSortOrder(value);
  };

  const handleSearchAccept = () => {
    if (trimmedSearchText.length >= 2) {
      setCommittedSearchText(trimmedSearchText);
      setSelectedGames([]);
      // setSelectAllChecked(false);
      setGridMode('search');
      setPropositionGame(null);
    } else {
      setCommittedSearchText('');
      setGridMode('explorer');
      setPropositionGame(null);
    }
  };

  // const onSelectAllCheckedChange = async (checked: boolean) => {
  //   if (!checked) {
  //     setSelectedGames([]);
  //     setSelectAllChecked(false);
  //     setSelectAllRequested(false);
  //     return;
  //   }

  //   setSelectAllChecked(true);
  //   setSelectAllRequested(true);
  // };

  return (
    <Root className="wide-scrollbar">
      <ResourcesPageHeader
        title={
          <FormattedMessage
            id="searchWithinGames"
            defaultMessage="Search within Games"
          />
        }
        titleAction={
          <ExportToExcelButton
            filterParams={filterParams}
            q={trimmedCommittedSearchText}
          />
        }
        searchPlaceholder={intl.formatMessage({
          id: 'searchGamesByNameOrId',
          defaultMessage: 'Search games by name or ID',
        })}
        sortBy={sortBy}
        searchText={searchText}
        onSortByChange={handleSortByChange}
        onSearchTextChange={setSearchText}
        options={GamesSortByOptions}
        sortOrder={sortOrder}
        onSortOrderChange={handleSortOrderChange}
        defaultSortBy={defaultSortBy}
        defaultSortOrder={defaultSortOrder}
        loading={isSearchSuggestionsLoading}
        searchPropositionsPopperRef={searchPropositionsPopperRef}
        onSearchAccept={handleSearchAccept}
        onSearchCancel={() => {
          setSearchText('');
          setCommittedSearchText('');
          setSelectedGames([]);
          setGridMode('explorer');
          setPropositionGame(null);
        }}
        propositions={
          hasSufficientSearchText && searchSuggestions.length ? (
            <ResourcesSearchPropositions
              variant="games"
              games={searchSuggestions}
              onGamesClick={(game) => {
                setGridMode('proposition');
                setCommittedSearchText('');
                setPropositionGame(game);
              }}
            />
          ) : null
        }
      />
      {/* <StyledGamesActionBar
        filesLength={total_count || 0}
        selectedLength={selectedGames.length}
        selectAllChecked={selectAllChecked}
        onSelectAllCheckedChange={onSelectAllCheckedChange}
        onDownloadGameSheets={() => handleDownload('game_sheets')}
        loading={loading || gamesAllQuery.isFetching}
      /> */}
      <GameGrid>
        {data?.map((groupGames, index) => {
          const defaultGame =
            getFirstGameByRTP(
              groupGames,
              filters.gameStatus,
              sortBy,
              sortOrder,
            ) || groupGames[0];
          const gameIds = groupGames.map((g) => g.id);
          const isSelected = gameIds.some((id) =>
            selectedGames.includes(id),
          );
          return (
            <GameCard
              key={defaultGame.id || index}
              games={groupGames}
              selected={isSelected}
              onSelect={() => handleGameSelect(groupGames)}
              defaultGame={defaultGame}
              section="Games Resources"
            />
          );
        })}
        {!isLoading &&
          !isFetchingNextPage &&
          (!data || data.length === 0) &&
          hasActiveSearchOrFilter && (
            <NoResultsContainer>
              <NoResultsText>
                <FormattedMessage
                  id="noSearchResults"
                  defaultMessage="No search results."
                />
              </NoResultsText>
            </NoResultsContainer>
          )}
        {(isLoading || isFetchingNextPage) && (
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        )}
      </GameGrid>
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  padding: `0 ${theme.spacing(4)} ${theme.spacing(4)}`,
}));

const GameGrid = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 26,
  width: '100%',
  minHeight: 400,
  marginTop: theme.spacing(4),
}));

const LoaderContainer = styled('div')(() => ({
  width: '100%',
  padding: '60px',
  display: 'flex',
  justifyContent: 'center',
}));

const NoResultsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(8),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const NoResultsText = styled('div')(({ theme }) => ({
  fontSize: '16px',
  lineHeight: '24px',
  color: theme.palette.textSecondaryDarkGrey,
  fontWeight: 400,
}));

// const StyledGamesActionBar = styled(GamesActionBar)(() => ({
//   marginTop: 17,
//   marginBottom: 17,
// }));

export default GamesResourcesPage;
